import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Search,
  Trash2,
  RefreshCcw,
  ShieldCheck,
  UserRound,
  Shield,
  Upload,
  Download,
} from "lucide-react";
import { UserTable } from "@/components/users/UserTable";
import { UserTableSkeleton } from "@/components/users/UserTableSkeleton";
import { CreateUserDialog } from "@/components/users/CreateUserDialog";
import { EditUserSheet } from "@/components/users/EditUserSheet";
import { User } from "@/types/user";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleTable } from "@/components/roles/RoleTable";
import { RoleTableSkeleton } from "@/components/roles/RoleTableSkeleton";
import { CreateRoleDialog } from "@/components/roles/CreateRoleDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserApi, RoleApi } from "@/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ProgramApi } from "@/api/program.api";
import { MerchantApi } from "@/api/merchant.api";
import { ClientApi } from "@/api/client.api";
import { BulkUploadModal } from "@/components/merchant/BulkUploadModal";
import { CSV_UPLOAD_TYPES } from "@/constants/common.constant";
import { useDebounce } from "@/hooks/use-debounce";

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [roles, setRoles] = useState<any[]>([]);
  const [fetchedRoles, setFetchedRoles] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // 500ms delay
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateRoleDialogOpen, setIsCreateRoleDialogOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [exportingUsersCsv, setExportingUsersCsv] = useState(false);
  const [possiblePermissions, setPossiblePermissions] = useState<any[]>([]);

  // Confirmation dialog states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [merchants, setMerchants] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);

  const [isBulkUserUploadOpen, setIsBulkUserUploadOpen] = useState(false);
  const [isUserErrorModalOpen, setIsUserErrorModalOpen] = useState(false);
  const [userUploadFailures, setUserUploadFailures] = useState<any[]>([]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const apiParams: any = {
      roleId: selectedRoleId || "",
      pageNumber: currentPage,
      pageSize: rowsPerPage,
      sortBy: "createdDate",
      sortOrder: "desc",
    };

    // Only include username parameter if debouncedSearchQuery has a value
    if (debouncedSearchQuery.trim()) {
      apiParams.username = debouncedSearchQuery;
    }

    UserApi.fetchUsersFromDB(apiParams)
      .then((res: any) => {
        const data = res.data;
        setTotalUsers(data.totalNumberOfUsers || 0);
        setUsers(
          (data.userVOs || []).map((u: any) => ({
            id: u.id?.toString() ?? "",
            username: u.username ?? "",
            name: u.name ?? "",
            email: u.username ?? "", // No email in response, fallback to username
            role: u.roleVO?.roleCode?.toLowerCase() ?? "user",
            avatarUrl: u.userProfileImageUri ?? "",
            createdAt: u.createdDate ?? "",
            // Only show actual last login if backend provides it
            lastLogin: u.lastLoginDate ?? "",
            phoneCountryCode: u.phoneCountryCode ?? "",
            phoneNumber: u.phoneNumber ?? "",
            roleVO: u.roleVO
              ? {
                  id: u.roleVO.id,
                  roleName: u.roleVO.roleName,
                  roleCode: u.roleVO.roleCode,
                }
              : undefined,
          }))
        );
      })
      .catch(() => {
        setUsers([]);
        setTotalUsers(0);
      })
      .finally(() => setLoading(false));
  }, [currentPage, rowsPerPage, debouncedSearchQuery, selectedRoleId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    RoleApi.getAllRoles()
      .then((res: any) => {
        const apiRoles = Array.isArray(res.data) ? res.data : [];
        setFetchedRoles(apiRoles);
        setRoles(
          apiRoles.map((r: any) => ({
            id: r.id?.toString() ?? "",
            name: r.roleName ?? "",
            description: r.description || "Sample description",
            users: r.countOfUsers ?? 0,
          }))
        );
      })
      .catch(() => { setRoles([]); setFetchedRoles([]); });
  }, []);

  useEffect(() => {
    // Fetch merchants
    MerchantApi.getAllMerchants()
      .then((res: any) => {
        setMerchants(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setMerchants([]));

    // Fetch programs
    ProgramApi.getProgramsFromDB()
      .then((res: any) => {
        setPrograms(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setPrograms([]));

    // Fetch clients
    ClientApi.getAllClients()
      .then((res: any) => {
        setClients(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setClients([]));
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0); // Reset to first page on new search (0-base indexing)
  };

  const handleExportUsersCsv = async () => {
    setExportingUsersCsv(true);
    try {
      const payload = {
        roleId: selectedRoleId || "",
        username: searchQuery.trim(),
        pageNumber: 0,
        pageSize: 1000,
      };
      const response = await UserApi.getUsersFromDbInCSVFormat(payload);
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `users_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Users exported as CSV successfully");
    } catch (error: unknown) {
      let message = "Failed to export users as CSV";
      const err = error as {
        response?: { status?: number; data?: unknown };
      };
      if (err.response?.status === 400 && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text) as { errorMessage?: string };
          if (parsed.errorMessage) {
            message = parsed.errorMessage;
          }
        } catch {
          // keep default message
        }
      }
      toast.error(message);
    } finally {
      setExportingUsersCsv(false);
    }
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      role.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(totalUsers / rowsPerPage);

  const handleAddUser = (newUser: User) => {
    setUsers([...users, newUser]);
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    setSelectedUserId(userId);
    setSelectedUserName(user?.name || "");
    setDeleteConfirmOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUserForEdit(user);
    setIsEditSheetOpen(true);
  };

  const handleUserUpdated = (updatedUser: User) => {
    // Refetch users from database to show updated values
    fetchUsers();
  };

  const confirmDeleteUser = async () => {
    if (selectedUserId) {
      try {
        await UserApi.deleteUser(selectedUserId);
        setUsers(users.filter((user) => user.id !== selectedUserId));
        toast.success("User deleted successfully");
      } catch (error) {
        toast.error("Failed to delete user");
        console.error("Delete user error:", error);
      }
    }
    setDeleteConfirmOpen(false);
    setSelectedUserId(null);
    setSelectedUserName("");
  };

  const handleAddRole = (newRole: any) => {
    setRoles([...roles, newRole]);
    toast.success("Role created successfully");
  };

  const handleDeleteRole = (roleId: string) => {
    setRoles(roles.filter((role) => role.id !== roleId));
    toast.success("Role deleted successfully");
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(Number(value));
    setCurrentPage(0); // Reset to first page when changing rows per page
  };

  const handleOpenCreateUser = async () => {
    setCreateUserLoading(true);
    try {
      const [programsRes, merchantsRes, clientsRes, rolesRes] =
        await Promise.all([
          ProgramApi.getProgramsFromDB(),
          MerchantApi.getAllMerchants(),
          ClientApi.getAllClients(),
          RoleApi.getAllRoles(),
        ]);
      setFetchedRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setIsCreateDialogOpen(true);
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Handle bulk user upload result (success + detailed errors)
  const handleBulkUserUpload = (uploadResult: any) => {
    if (!uploadResult) {
      toast.error("Failed to upload users. Please try again.");
      return;
    }

    const {
      successCount = 0,
      failureCount = 0,
      failures = [],
    } = uploadResult || {};

    // Success summary
    if (successCount > 0 && failureCount === 0) {
      toast.success(`Successfully uploaded ${successCount} user(s).`);
    } else if (successCount > 0 && failureCount > 0) {
      toast.success(
        `Uploaded ${successCount} user(s) with ${failureCount} error(s).`
      );
    } else if (successCount === 0 && failureCount === 0) {
      toast.success("No records found in the uploaded file.");
    }

    // Failure details
    if (failureCount > 0 && Array.isArray(failures) && failures.length > 0) {
      setUserUploadFailures(failures);
      setIsUserErrorModalOpen(true);
      toast.error(
        `Failed to upload ${failureCount} record(s). Please review the error details.`
      );
    }

    // Always refresh user list after upload attempt
    fetchUsers();
    setIsBulkUserUploadOpen(false);
  };

  // Call RoleApi.getAllRoles() and log output when Roles tab is selected
  React.useEffect(() => {
    if (activeTab === "roles") {
      RoleApi.getAllPermissions()
        .then((res) => {
          // Structure the result to include permission codes for each type
          const structured = Array.isArray(res.data)
            ? res.data.map((item) => ({
                featureCode: item.featureCode,
                featureId: item.featureId,
                featureName: item.featureName,
                viewPermissionCode: item.viewPermission?.permissionCode,
                updatePermissionCode: item.updatePermission?.permissionCode,
                createPermissionCode: item.createPermission?.permissionCode,
                deletePermissionCode: item.deletePermission?.permissionCode,
              }))
            : [];
          setPossiblePermissions(structured);
        })
        .catch((err) => {
          console.error("Permissions API error:", err);
        });
    }
  }, [activeTab]);

  console.log(possiblePermissions, "possiblePermissions");

  return (
    <>
      <Card className="w-full border-0 shadow-sm card-modern">
        <CardHeader className="bg-white rounded-t-lg border-b border-gray-100 py-3 px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-2xl font-semibold">
              User & Role Management
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              {activeTab === "users" ? (
                <div className="flex gap-2">
                  <Button
                    onClick={handleOpenCreateUser}
                    className="flex items-center gap-2"
                    disabled={createUserLoading}
                  >
                    <Plus size={16} />{" "}
                    {createUserLoading ? "Loading..." : "Add New User"}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex items-center justify-center"
                    onClick={() => setIsBulkUserUploadOpen(true)}
                    title="Bulk Upload"
                  >
                    <Upload size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="flex items-center justify-center"
                    onClick={handleExportUsersCsv}
                    disabled={exportingUsersCsv}
                    title={exportingUsersCsv ? "Exporting CSV…" : "Export CSV"}
                  >
                    <Download size={16} />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setIsCreateRoleDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus size={16} /> Add New Role
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                {activeTab === "users" ? "Search Users" : "Search Roles"}
              </h3>
              {activeTab === "users" && (searchQuery || selectedRoleId) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedRoleId("");
                    setCurrentPage(0);
                  }}
                  className="flex items-center gap-2"
                >
                  <RefreshCcw size={14} />
                  Reset
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {activeTab === "users" && (
                <Select
                  value={selectedRoleId || "all"}
                  onValueChange={(val) => {
                    setSelectedRoleId(val === "all" ? "" : val);
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-[200px] shrink-0">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {fetchedRoles.map((r: any) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.roleName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <Input
                  placeholder={
                    activeTab === "users"
                      ? "Search by username"
                      : "Search by role"
                  }
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <Tabs
            defaultValue="users"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="mb-4">
              <TabsList className="mb-0">
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <UserRound size={16} /> Users
                </TabsTrigger>
                <TabsTrigger value="roles" className="flex items-center gap-2">
                  <Shield size={16} /> Roles
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="users" className="mt-0">
              {loading ? (
                <UserTableSkeleton />
              ) : (
                <UserTable
                  users={users}
                  onDeleteUser={handleDeleteUser}
                  onEditUser={handleEditUser}
                />
              )}
            </TabsContent>

            <TabsContent value="roles" className="mt-0">
              {loading ? (
                <RoleTableSkeleton />
              ) : (
                <RoleTable
                  roles={filteredRoles}
                  onDeleteRole={handleDeleteRole}
                  refetchRoles={() => {}}
                  possiblePermissions={possiblePermissions}
                />
              )}
            </TabsContent>
          </Tabs>

          {/* Pagination controls */}
          {!loading && totalUsers > 0 && activeTab === "users" && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={handleRowsPerPageChange}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder={rowsPerPage.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <EnhancedPaginationControl
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Dialog */}
      <CreateUserDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onAddUser={handleAddUser}
        roles={fetchedRoles}
        merchants={merchants}
        programs={programs}
        clients={clients}
      />

      {/* Edit User Sheet */}
      <EditUserSheet
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        user={selectedUserForEdit}
        onUserUpdated={handleUserUpdated}
      />

      {/* Role Dialog */}
      <CreateRoleDialog
        open={isCreateRoleDialogOpen}
        onOpenChange={setIsCreateRoleDialogOpen}
        onAddRole={handleAddRole}
      />

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{selectedUserName}</strong>? This action cannot be undone
              and will permanently remove the user from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk User Upload Modal */}
      <BulkUploadModal
        uploadType={CSV_UPLOAD_TYPES.USER}
        open={isBulkUserUploadOpen}
        onOpenChange={setIsBulkUserUploadOpen}
        title="Bulk Upload Users"
        type="user"
        onUploadComplete={handleBulkUserUpload}
      />

      {/* Bulk User Upload Error Details Modal */}
      <Dialog
        open={isUserErrorModalOpen}
        onOpenChange={setIsUserErrorModalOpen}
      >
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Errors</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {userUploadFailures && userUploadFailures.length > 0 ? (
              userUploadFailures.map((failure, index) => (
                <div
                  key={index}
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  <div className="font-medium">#{index + 1}</div>
                  <div>{failure?.reason || "Unknown error"}</div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-600">
                No detailed error information available.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsUserErrorModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Users;
